import { AdBreakDialog } from '@gagovictor/shared-frontend-core/dist/ads';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';

import { useT } from '../i18n';
import { RootState } from '../store';

import {
    showInterstitial,
    initializeAdMob,
    showRewardedVideo,
    RewardedResult,
} from './admob';
import { config, isNative, webBreakAdConfigured } from './config';

interface AdsContextValue {
    /** False once the lifetime unlock is owned. */
    adsEnabled: boolean;
    /** Shows a full-screen ad (phone) or an ad break (web) and resolves when it is closed. Resolves at once when ads are off. */
    showAdBreak: () => Promise<void>;
    /**
     * A rewarded video (phone) or an ad break (web). 'rewarded' when it was watched or no ad is due (ads off),
     * 'dismissed' when closed early, 'unavailable' when none could be shown (the caller lets the person through).
     */
    showRewardedBreak: () => Promise<RewardedResult>;
}

const AdsContext = createContext<AdsContextValue>({
    adsEnabled: false,
    showAdBreak: async () => undefined,
    showRewardedBreak: async () => 'rewarded',
});

export const useAds = () => useContext(AdsContext);

export const AdsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const t = useT();
    const lifetime = useSelector((s: RootState) => s.tomato.lifetime);
    const adsEnabled = !lifetime;
    const [breakOpen, setBreakOpen] = useState(false);
    const pending = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (adsEnabled) void initializeAdMob();
    }, [adsEnabled]);

    const closeBreak = useCallback(() => {
        setBreakOpen(false);
        pending.current?.();
        pending.current = null;
    }, []);

    const showAdBreak = useCallback(async () => {
        if (!adsEnabled) return;
        if (isNative()) return showInterstitial();
        if (!webBreakAdConfigured()) return;
        // A second request while a break is open just waits for the same close.
        await new Promise<void>((resolve) => {
            const previous = pending.current;
            pending.current = () => {
                previous?.();
                resolve();
            };
            setBreakOpen(true);
        });
    }, [adsEnabled]);

    const showRewardedBreak = useCallback(async (): Promise<RewardedResult> => {
        if (!adsEnabled) return 'rewarded';
        if (isNative()) return showRewardedVideo();
        if (!webBreakAdConfigured()) return 'unavailable';
        await showAdBreak();
        return 'rewarded';
    }, [adsEnabled, showAdBreak]);

    const value = useMemo(
        () => ({ adsEnabled, showAdBreak, showRewardedBreak }),
        [adsEnabled, showAdBreak, showRewardedBreak]
    );

    return (
        <AdsContext.Provider value={value}>
            {children}
            {/* The shared core dialog: fullscreen on a phone, and locked until the ad has been viewable for a few seconds. */}
            <AdBreakDialog
                open={breakOpen}
                onClose={closeBreak}
                client={config.adsenseClient}
                slot={config.adsenseBreakSlot}
                preview={config.adsPreview}
                test={config.adsenseTest}
                labels={{
                    title: t('ads.breakTitle'),
                    preview: t('ads.breakPreview'),
                    continue: t('ads.continue'),
                    continueIn: (seconds) => t('ads.continueIn', { seconds }),
                }}
            />
        </AdsContext.Provider>
    );
};
